import React, { useState, useEffect, useRef } from "react";
import { IoSend } from "react-icons/io5";
import SelectChatHeadPage from "../components/SelectChatHeadPage";
import { getChatLists, sendChatMessage } from "../../../API/ChatAPI/ChatApi"; // Adjust the import path if necessary
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { MdAttachFile, MdClose } from "react-icons/md";
import { uploadFiles } from "../../../API/FileAPI/FileAPI";

const ChatList = ({
  chatList,
  setChatList,
  chatsHeads,
  setChatsHeads,
  selectedChat,
}) => {
  const { AdminProfile } = useSelector((state) => state?.adminProfile);
  const [message, setMessage] = useState("");
  const [upperloading, setUpperLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1); // Track the current page for pagination
  const pageSize = 10;
  const textareaRef = useRef(null);

  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Create a temporary URL for immediate preview
      const blobUrl = URL.createObjectURL(file);
      setUploadedFiles([{ url: blobUrl, file }]);

      // Reset file input value
      event.target.value = "";
    }
  };

  const removeFile = () => {
    setUploadedFiles([]);
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    textarea.style.height = "45px"; // Reset to min height
    textarea.style.height = `${textarea.scrollHeight}px`; // Expand based on content
  };

  const handleInputChange = (e) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      if (message.trim()) {
        handleSendMessage(message);
        setMessage(""); // Clear message
        textareaRef.current.style.height = "45px"; // Reset height to min
      }
    }
  };

  const chatEndRef = useRef(null); // Create a ref to track the bottom of the chat
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const totalImages = chatList.filter((msg) => msg.imageMessage).length;

  useEffect(() => {
    if (imagesLoaded === totalImages) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [imagesLoaded, totalImages]);

  const handleImageLoad = () => {
    setImagesLoaded((prevCount) => prevCount + 1);
  };

  // Function to scroll to bottom
  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  };

  // Scroll on chatList update or new page load
  useEffect(() => {
    if (page === 1 || chatList.length === 0) {
      scrollToBottom();
    }
  }, [chatList, page]);

  // Handle image load to ensure full bottom scroll
  useEffect(() => {
    const images = document.querySelectorAll(".chat-message img");
    let loadedImages = 0;

    if (images.length === 0) {
      scrollToBottom();
      return;
    }

    images.forEach((img) => {
      if (img.complete) {
        loadedImages++;
      } else {
        img.onload = () => {
          loadedImages++;
          if (loadedImages === images.length) {
            scrollToBottom();
          }
        };
      }
    });
  }, [chatList]);

  function generateUniqueId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }
  const handleSendMessage = async () => {
    const uniqueId = generateUniqueId();
    try {
      if ((uploadedFiles.length > 0 || message.trim()) && selectedChat) {
        let messageIs = message;
        setChatList([
          ...chatList,
          {
            _id: uniqueId,
            receiverId: selectedChat.otherUser._id,
            type: 0,
            textMessage: message ? message : null,
            imageMessage:
              uploadedFiles.length > 0 ? uploadedFiles[0].url : null,
            senderId: AdminProfile._id,
            createdAt: Date.now(),
          },
        ]);
        setMessage("");
        scrollToBottom(); // Scroll to the bottom after sending a message
        const uploadedFILE = uploadedFiles;
        removeFile();
        let response;
        // Upload the file to the server
        if (uploadedFILE.length > 0) {
          response = await uploadFiles(uploadedFILE[0].file);
        }
        const sendMessage = await sendChatMessage({
          receiverId: selectedChat.otherUser._id,
          type: 0,
          textMessage: messageIs ? messageIs : null,
          imageMessage: response ? response.photos[0] : null,
        });
        setChatList((prev) => {
          return prev.map((item) => {
            if (item._id === uniqueId) {
              return sendMessage;
            } else {
              return item;
            }
          });
        });
        const updatedChats = [
          ...chatsHeads.filter(
            (chat) =>
              selectedChat.otherUser._id.toString() ===
              chat.otherUser._id.toString() // Move specific chat to top
          ),
          ...chatsHeads.filter(
            (chat) =>
              selectedChat.otherUser._id.toString() !==
              chat.otherUser._id.toString() // Keep other chats as is
          ),
        ];
        setChatsHeads(updatedChats);
        setChatsHeads((prev) => {
          prev = prev.map((item) => {
            if (
              item.otherUser._id.toString() ===
              selectedChat.otherUser._id.toString()
            ) {
              item.textMessage = messageIs;
              return item;
            } else {
              return item;
            }
          });
          return prev;
        });
      }
    } catch (error) {
      // remove the chat that was added
      setChatList((prev) => {
        return prev.filter((item) => item._id !== uniqueId);
      });
      toast.error("Error sending message: ");
      console.log(error);
    }
  };

  // Fetch chat lists and handle pagination
  useEffect(() => {
    const fetchChatList = async () => {
      setLoading(true);
      try {
        const data = await getChatLists({
          otherId: selectedChat.otherUser._id,
          page,
          pageSize: pageSize,
        });
        if (data.chats) {
          setChatList((prevChats) => [...data.chats, ...prevChats]); // Prepend new chats
          setHasMore(data.paginationInfo.totalPages > page);
        }
      } catch (e) {
        console.log("Error fetching chat list:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchChatList();
  }, [selectedChat, page, setChatList]);

  useEffect(() => {
    setChatList([]);
    setPage(1);
  }, [selectedChat, setChatList]);

  // Load more chats when user scrolls to the top
  const loadMoreChats = async () => {
    if (!upperloading && hasMore) {
      try {
        setUpperLoading(true);

        // Save the current scroll height before adding more chats
        const chatContainer = chatEndRef.current.parentElement;
        const currentScrollHeight = chatContainer.scrollHeight;

        const nextPage = page + 1;
        setPage(nextPage);

        // Wait until new chats are loaded
        setTimeout(() => {
          // Adjust scroll position to maintain the view
          chatContainer.scrollTop =
            chatContainer.scrollHeight - currentScrollHeight;
        }, 100);
      } catch (error) {
        console.error("Error loading more chats:", error);
      } finally {
        setUpperLoading(false);
      }
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-white">
      {selectedChat ? (
        <>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
            <div className="flex items-center gap-3">
              {selectedChat?.otherUser?.profilePhoto ? (
                <img
                  className="w-12 h-12 rounded-full object-cover"
                  src={selectedChat?.otherUser?.profilePhoto}
                  alt="profileimage"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[--primary] flex items-center justify-center text-white font-medium">
                  {selectedChat?.otherUser?.firstName?.charAt(0)}{selectedChat?.otherUser?.lastName?.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="font-semibold text-lg text-gray-900">
                  {selectedChat?.otherUser?.firstName}{" "}
                  {selectedChat?.otherUser?.lastName}
                </h1>
                <p className="text-sm text-gray-500">Online</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50">
            {hasMore && !upperloading && !loading && (
              <div className="flex justify-center mb-4">
                <button
                  className="bg-[--primary] text-white rounded-lg py-2 px-4 hover:bg-orange-600 transition-colors text-sm font-medium"
                  onClick={loadMoreChats}
                >
                  Load previous messages
                </button>
              </div>
            )}

            {upperloading && (
              <div className="flex justify-center my-4">
                <div className="bg-gray-200 text-gray-600 rounded-lg py-2 px-4 text-sm">
                  Loading previous messages...
                </div>
              </div>
            )}

            {loading && (
              <div className="flex justify-center my-4">
                <div className="bg-gray-200 text-gray-600 rounded-lg py-2 px-4 text-sm">
                  Loading messages...
                </div>
              </div>
            )}

            {chatList.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <div className="text-4xl mb-2">💬</div>
                <p className="text-sm">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              chatList?.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${
                    msg?.senderId === AdminProfile?._id?.toString()
                      ? "justify-end"
                      : "justify-start"
                  } mb-4 gap-3`}
                >
                  {!(msg?.senderId === AdminProfile?._id?.toString()) && (
                    <div className="flex-shrink-0">
                      {selectedChat?.otherUser?.profilePhoto ? (
                        <img
                          className="w-8 h-8 rounded-full object-cover"
                          src={selectedChat?.otherUser?.profilePhoto}
                          alt="profileimage"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[--primary] flex items-center justify-center text-white text-xs font-medium">
                          {selectedChat?.otherUser?.firstName?.charAt(0)}{selectedChat?.otherUser?.lastName?.charAt(0)}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div
                    className={`flex flex-col max-w-[70%] ${
                      msg?.senderId === AdminProfile?._id?.toString()
                        ? "items-end"
                        : "items-start"
                    }`}
                  >
                    {msg?.imageMessage && (
                      <div className="mb-2">
                        <img
                          src={msg.imageMessage}
                          className="max-w-full h-auto rounded-lg shadow-sm"
                          alt={msg.imageMessage}
                          onLoad={handleImageLoad}
                        />
                      </div>
                    )}

                    {msg?.textMessage && (
                      <div
                        className={`px-4 py-3 rounded-2xl break-words whitespace-pre-wrap ${
                          msg?.senderId === AdminProfile?._id?.toString()
                            ? "bg-[--primary] text-white rounded-br-md"
                            : "bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-200"
                        }`}
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {msg.textMessage}
                      </div>
                    )}

                    <div className="text-xs text-gray-500 mt-1 px-1">
                      {new Date(msg.createdAt).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </div>
                  </div>

                  {msg?.senderId === AdminProfile?._id?.toString() && (
                    <div className="flex-shrink-0">
                      {AdminProfile?.profileImage ? (
                        <img
                          className="w-8 h-8 rounded-full object-cover"
                          src={AdminProfile?.profileImage}
                          alt="profileimage"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-medium">
                          {AdminProfile?.firstName?.charAt(0)}{AdminProfile?.lastName?.charAt(0)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Add the ref to this div to scroll into view */}
            <div ref={chatEndRef}></div>
          </div>
          {/* Input */}
          <div className="relative bg-white border-t border-gray-200">
            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
              <div className="absolute z-10 top-[-100px] left-4 flex gap-2 p-3 bg-white rounded-lg shadow-lg border border-gray-200">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="relative flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-200"
                  >
                    <img src={file.url} alt="file" className="w-16 h-16 object-cover rounded" />
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <MdClose className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="p-4">
              <div className="relative flex items-end gap-3">
                <label className="flex-shrink-0 p-2 text-gray-500 hover:text-[--primary] cursor-pointer transition-colors">
                  <MdAttachFile className="w-5 h-5" />
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept="image/*"
                  />
                </label>
                
                <div className="flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    placeholder="Type a message..."
                    value={message}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    className="w-full py-3 px-4 border border-gray-300 rounded-2xl outline-none focus:ring-2 focus:ring-[--primary] focus:border-transparent resize-none overflow-hidden bg-gray-50"
                    rows={1}
                    style={{ minHeight: "48px", maxHeight: "120px" }}
                  />
                </div>
                
                <button
                  onClick={handleSendMessage}
                  className="flex-shrink-0 bg-[--primary] text-white rounded-full p-3 flex items-center justify-center hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!message.trim() && uploadedFiles.length === 0}
                >
                  <IoSend className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <SelectChatHeadPage />
      )}
    </div>
  );
};

export default ChatList;
